import { useEffect, useMemo, useRef, useState } from "react";
import TkDatePicker from "../../../components/atoms/TkDatePicker";
import TkDataTable from "../../../components/molecules/TkDataTable";
import { IAPI_STA025_Response } from "../../../type/apiType";
import TkButton from "../../../components/atoms/TkButton";
import TkHelpIcon from "../../../components/atoms/TkHelpIcon";
import { Box } from "@mui/material";
import "../_style.scss";

interface props {
  res: IAPI_STA025_Response;
  onClick: (e: any) => void;
  onChange: (e: any) => void;
  setDateErrorFlg: React.Dispatch<React.SetStateAction<boolean>>;
}

const monthMap = [
  { label: "apr", value: 4 },
  { label: "may", value: 5 },
  { label: "jun", value: 6 },
  { label: "jul", value: 7 },
  { label: "aug", value: 8 },
  { label: "sep", value: 9 },
  { label: "oct", value: 10 },
  { label: "nov", value: 11 },
  { label: "dec", value: 12 },
  { label: "jan", value: 1 },
  { label: "feb", value: 2 },
  { label: "mar", value: 3 },
];

const StaffListTable: React.FC<props> = ({
  res,
  onClick,
  onChange,
  setDateErrorFlg,
}) => {
  const [staffList, setStaffList] = useState(res.staffList ?? []);
  const saveStaffList = useRef(res.staffList ?? []);
  const updatedValuesSet = useRef(new Map<string, string>());
  const pageValueRef = useRef(0);
  const pageValue = pageValueRef.current;
  const setPageValue = (value: number) => {
    pageValueRef.current = value;
  };

  useEffect(() => {
    if (res.staffList && res.staffList.length > 0) {
      res.staffList.forEach((item: any) => {
        item.year = res.unrecognizedYear;
      });
    }
    setStaffList(res.staffList ?? []);
    saveStaffList.current = res.staffList ?? [];
    setPageValue(0);
    onChange && onChange(res.staffList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res.staffList, res.unrecognizedYear]);

  const checkNeedNaShi = (item: any) => {
    return monthMap.some((m) => {
      let month = m.label as keyof typeof item;
      return item[month] === "1" || item[month] === "3";
    });
  };

  const handleChange = (e: any, key: any, item: any) => {
    const newArr = saveStaffList.current.map((staff) =>
      staff.staffNo === item.staffNo
        ? { ...staff, [key]: e?.target?.value }
        : staff
    );

    saveStaffList.current = newArr;
    updatedValuesSet.current.set(item.staffNo, e?.target?.value);
    setStaffList(newArr);
    onChange && onChange(newArr);
  };

  const headList: any[] = [
    {
      headTitle: (
        <>
          氏名
          <TkHelpIcon
            helpMessage={
              <>
                登録されている被共済職員の氏名です。外字や「髙（はしごだか）」、「﨑（たつさき）」等の常用漢字でない文字は、正しく表示されていない恐れがあります。
              </>
            }
          />
        </>
      ),
      className:
        "SCRC060112DataTableSection__header SCRC060112DataTableSection__staffNo_header",
    },
    {
      headTitle: "生年月日",
      className:
        "SCRC060112DataTableSection__header SCRC060112DataTableSection__birthday_header",
    },
    {
      headTitle: "退職（予定）日",
      className:
        "SCRC060112DataTableSection__header SCRC060112DataTableSection__TkDatePicker_header",
    },
    {
      headTitle: "育休休業状況",
      dontNeedSort: true,
      className:
        "SCRC060112DataTableSection__header SCRC060112DataTableSection__TkButton_header",
    },
    {
      top: {
        headTitle: (
          <>
            {res.unrecognizedYear}年度　ならない月
            <TkHelpIcon
              helpMessage={
                <>
                  表示されている年度のならない月を登録してください。
                  <br />
                  ならない月とは、業務に従事した日数（従事したとみなす日を含む）が１０日を超えない月です。
                  <br />
                  詳しくは
                  <a
                    href={`${
                      process.env.PUBLIC_URL ?? ""
                    }/assets/pdf/≪別紙≫「なる月」「ならない月」について.pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    こちら
                  </a>
                  をご確認ください。
                </>
              }
            />
          </>
        ),
      },
      bottom: [
        {
          headTitle: "無",
        },
        {
          headTitle: "4月",
        },
        {
          headTitle: "5月",
        },
        {
          headTitle: "6月",
        },
        {
          headTitle: "7月",
        },
        {
          headTitle: "8月",
        },
        {
          headTitle: "9月",
        },
        {
          headTitle: "10月",
        },
        {
          headTitle: "11月",
        },
        {
          headTitle: "12月",
        },
        {
          headTitle: "1月",
        },
        {
          headTitle: "2月",
        },
        {
          headTitle: "3月",
        },
      ],
    },
  ];
  let bodyList: any = useMemo(
    () =>
      staffList?.map((item: any) => {
        return [
          {
            children: (item.staffNo ?? "") + ":" + (item.staffNameKanji ?? ""),
            forSort: item.staffNo,
            className: "SCRC060112DataTableSection__staffNo",
          },
          {
            children: item.birthday,
            forSort: item.birthday,
            className: "SCRC060112DataTableSection__birthday",
          },
          {
            children: (
              <TkDatePicker
                key={`${item.staffNo}`}
                wantToSetValue={
                  updatedValuesSet.current.has(item.staffNo)
                    ? updatedValuesSet.current.get(item.staffNo)
                    : item.retireDate ?? ""
                }
                maxDate={new Date("2099/12/31")}
                minDate={new Date("1900/1/1")}
                disabled={item.loackState !== "0"}
                onChange={(e: any) => handleChange(e, "retireDate", item)}
                onGetErrorFlg={(e: any) => setDateErrorFlg(e)}
              />
            ),
            width: "250px",
            className: "SCRC060112DataTableSection__TkDatePicker",
            forSort: item.retireDate,
          },
          {
            children: (
              <TkButton
                onClick={() => onClick(item)}
                disabled={item.loackState === "1"}
              >
                育休確認・登録
              </TkButton>
            ),
            align: "center",
            className: "SCRC060112DataTableSection__TkButton",
          },
          {
            children: (
              <div
                className={`TkfakeCheckBox disabled ${
                  !checkNeedNaShi(item) && "checkmark clickDisabled"
                }`}
              ></div>
            ),
            className: "SCRC060112DataTableSection__checkbox",
          },
          ...monthMap.map((m) => {
            const month = m.label as keyof typeof item;
            const itemValue = item[month];

            const classStrategy: any = {
              "1": "checkmark",
              "2": "disabled",
              "3": "checkmark clickDisabled",
            };

            const isLock = item.loackState === "1";
            const setClass = classStrategy[itemValue] || "";

            return {
              children: (
                <div
                  className={`TkfakeCheckBox ${setClass} ${
                    isLock ? "disabled" : ""
                  }`}
                  onClick={() =>
                    handleClickCheckBox(
                      itemValue !== "1",
                      item.staffNo,
                      m.label
                    )
                  }
                ></div>
              ),
              className: "SCRC060112DataTableSection__checkbox",
              forSort: itemValue,
            };
          }),
        ];
      })!,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staffList]
  );

  const handleClickCheckBox = (flag: any, staffNo: any, month: any) => {
    let returnArr: any = [];

    setStaffList(() => {
      returnArr = saveStaffList.current.map((item) =>
        item.staffNo === staffNo ? { ...item, [month]: flag ? "1" : "0" } : item
      );

      saveStaffList.current = returnArr;
      return returnArr;
    });

    onChange && onChange(returnArr);
  };

  const handleSortChange = (sort: any) => {
    if (!sort || sort.length === 0) return;

    const sortedArr = sort.map((item: any) => {
      return item[0].forSort;
    });

    const sortedStaffList = sortedArr
      .map((staffNo: any) =>
        staffList.find((staff) => staff.staffNo === staffNo)
      )
      .filter((staff: any) => staff !== undefined)
      .map((staff: any) => {
        if (staff && updatedValuesSet.current.has(staff.staffNo)) {
          return {
            ...staff,
            retireDate: updatedValuesSet.current.get(staff.staffNo),
          };
        }
        return staff;
      });

    saveStaffList.current = sortedStaffList;

    setStaffList(sortedStaffList);
  };

  const handlePageChange = (_: any, page: any) => {
    setPageValue(page - 1);
  };

  return (
    <Box className="SCRC060112DataTableSection">
      <TkDataTable
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        headList={headList}
        bodyList={bodyList}
        pagin={true}
        dontResetPage
        setPageValue={pageValue}
      />
    </Box>
  );
};

export default StaffListTable;
